/**
 * Session Management System
 * 
 * Handles:
 * - Persistent login with configurable inactivity timeout
 * - Session expiration tracking
 * - Automatic logout on inactivity
 * - Remember destination for post-login redirect
 */

import { getSupabaseClient } from "@/lib/supabase/loader";

/**
 * Session configuration
 */
export const SESSION_CONFIG = {
  // Session timeout in milliseconds - DISABLED for regular users
  // Regular users stay logged in indefinitely (Supabase JWT handles expiry)
  // Set to very high value to effectively disable inactivity logout
  INACTIVITY_TIMEOUT: 24 * 60 * 60 * 1000, // 24 hours (won't trigger in practice)
  
  // Admin session timeout (stricter - 15 minutes for sensitive operations)
  ADMIN_INACTIVITY_TIMEOUT: 15 * 60 * 1000,
  
  // Check interval for session expiry (1 minute)
  CHECK_INTERVAL: 60 * 1000,
  
  // Storage keys
  LAST_ACTIVITY_KEY: "aegis_last_activity",
  RETURN_TO_KEY: "aegis_return_to",
  SESSION_START_KEY: "aegis_session_start",
};

/**
 * Session manager class
 */
class SessionManager {
  private checkInterval: NodeJS.Timeout | null = null;
  private activityListeners: (() => void)[] = [];
  private isAdminSession = false;
  private initTime = 0;

  /**
   * Initialize session tracking
   */
  initialize(isAdmin = false) {
    this.isAdminSession = isAdmin;
    this.initTime = Date.now();
    this.updateActivity();
    this.startMonitoring();
    this.attachActivityListeners();
    
    // Record session start time
    sessionStorage.setItem(SESSION_CONFIG.SESSION_START_KEY, Date.now().toString());
  }

  /**
   * Clean up session tracking
   */
  cleanup() {
    this.stopMonitoring();
    this.detachActivityListeners();
    this.initTime = 0;
    sessionStorage.removeItem(SESSION_CONFIG.LAST_ACTIVITY_KEY);
    sessionStorage.removeItem(SESSION_CONFIG.SESSION_START_KEY);
  }

  /**
   * Update last activity timestamp
   */
  updateActivity() {
    sessionStorage.setItem(SESSION_CONFIG.LAST_ACTIVITY_KEY, Date.now().toString());
  }

  /**
   * Check if session has expired due to inactivity
   * Regular users: Never expires due to inactivity (Supabase JWT handles it)
   * Admin users: Expires after 15 minutes of inactivity
   */
  isExpired(): boolean {
    const lastActivity = sessionStorage.getItem(SESSION_CONFIG.LAST_ACTIVITY_KEY);
    if (!lastActivity) return true;

    // Regular users: Never expire on inactivity
    // Their session is managed by Supabase JWT token expiry
    if (!this.isAdminSession) {
      return false;
    }

    // Admin users: Check inactivity timeout
    const timeout = SESSION_CONFIG.ADMIN_INACTIVITY_TIMEOUT;
    const elapsed = Date.now() - parseInt(lastActivity, 10);
    return elapsed > timeout;
  }

  /**
   * Get remaining session time in milliseconds
   * Regular users: Returns max value (never expires)
   * Admin users: Returns time until inactivity timeout
   */
  getRemainingTime(): number {
    const lastActivity = sessionStorage.getItem(SESSION_CONFIG.LAST_ACTIVITY_KEY);
    if (!lastActivity) return 0;

    // Regular users: Never expire on inactivity
    if (!this.isAdminSession) {
      return Infinity;
    }

    // Admin users: Return remaining time
    const timeout = SESSION_CONFIG.ADMIN_INACTIVITY_TIMEOUT;
    const elapsed = Date.now() - parseInt(lastActivity, 10);
    return Math.max(0, timeout - elapsed);
  }

  /**
   * Save current location for post-login redirect
   */
  saveReturnDestination(path: string) {
    sessionStorage.setItem(SESSION_CONFIG.RETURN_TO_KEY, path);
  }

  /**
   * Get and clear saved return destination
   */
  getReturnDestination(): string | null {
    const dest = sessionStorage.getItem(SESSION_CONFIG.RETURN_TO_KEY);
    if (dest) {
      sessionStorage.removeItem(SESSION_CONFIG.RETURN_TO_KEY);
    }
    return dest;
  }

  /**
   * Start monitoring for session expiration
   */
  private startMonitoring() {
    this.checkInterval = setInterval(() => {
      // Grace period: Skip checks for first 30 seconds after initialization
      // This prevents false positives right after login
      if (this.initTime && Date.now() - this.initTime < 30000) {
        return;
      }
      
      if (this.isExpired()) {
        this.handleExpiration();
      }
    }, SESSION_CONFIG.CHECK_INTERVAL);
  }

  /**
   * Stop monitoring
   */
  private stopMonitoring() {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
  }

  /**
   * Handle session expiration
   */
  private async handleExpiration() {
    console.log("Session expired due to inactivity");
    
    // Save current location for redirect after re-login
    const currentPath = window.location.pathname;
    if (!currentPath.startsWith("/login") && !currentPath.startsWith("/register")) {
      this.saveReturnDestination(currentPath);
    }

    // Sign out
    await supabase.auth.signOut();
    
    // Cleanup will be handled by SessionProvider's SIGNED_OUT event
  }

  /**
   * Attach activity listeners to track user interaction
   */
  private attachActivityListeners() {
    const updateActivity = () => this.updateActivity();
    
    // Track various user interactions
    const events = ["mousedown", "keydown", "scroll", "touchstart"];
    events.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true });
      this.activityListeners.push(() => window.removeEventListener(event, updateActivity));
    });
  }

  /**
   * Detach activity listeners
   */
  private detachActivityListeners() {
    this.activityListeners.forEach(cleanup => cleanup());
    this.activityListeners = [];
  }

  /**
   * Force logout (for admin re-authentication)
   */
  async forceLogout() {
    await supabase.auth.signOut();
    this.cleanup();
  }
}

// Singleton instance
export const sessionManager = new SessionManager();

/**
 * Configure Supabase session persistence
 */
export async function configureSessionPersistence() {
  // Supabase automatically handles session persistence via localStorage
  // We just need to ensure PKCE flow is enabled (already configured in client.ts)
  
  // Set session refresh interval
  // Supabase will auto-refresh tokens before expiry
  const { data: { session } } = await supabase.auth.getSession();
  
  return session;
}

/**
 * Check if user should be prompted for re-authentication
 * (Used for sensitive admin actions)
 */
export function requiresReAuthentication(): boolean {
  const sessionStart = sessionStorage.getItem(SESSION_CONFIG.SESSION_START_KEY);
  if (!sessionStart) return true;

  // Require re-auth after 1 hour for sensitive actions
  const elapsed = Date.now() - parseInt(sessionStart, 10);
  const oneHour = 60 * 60 * 1000;
  
  return elapsed > oneHour;
}
